/**
 * PropertyCard v2 — Realty237 redesign (Feb 2026).
 *
 * Design tokens applied:
 * - Flat surface (white, border-stone-200, no default shadow)
 * - Hover : translateY(-4px) + soft ambient shadow + image scale-105
 * - Aspect 4:3 image with absolute badges (top-left + top-right favorite)
 * - Price in Cabinet Grotesk display font, brand-600 color
 * - Title / location in Manrope
 * - Subtle separator + bottom row with beds/baths/m² + agent micro-avatar
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, Heart, ShieldCheck, Star, TrendingUp, Sparkles, Camera, Scale, Landmark, Clock, Eye, Play, ImageOff, Lock } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatPrice } from '../data/mockData';
import { useCurrency } from '../contexts/CurrencyContext';
import { useToast } from '../hooks/use-toast';
import { useCompare } from '../hooks/useCompare';
import { useFavorites } from '../hooks/useFavorites';
import { cdnImage, cdnSrcSet } from '../utils/cdnImage';

const isActive = (dateStr) => {
  if (!dateStr) return false;
  try {
    return new Date(dateStr).getTime() > Date.now();
  } catch {
    return false;
  }
};

const isRecent = (dateStr, withinDays = 7) => {
  if (!dateStr) return false;
  try {
    const ms = Date.now() - new Date(dateStr).getTime();
    return ms >= 0 && ms < withinDays * 86400000;
  } catch {
    return false;
  }
};

const PropertyCard = ({ property, onPayClick, payPrice, payDeadlineAt, onFavoriteChange }) => {
  const { format: formatCurrency, code: currencyCode } = useCurrency();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { add: addCompare, remove: removeCompare, has: hasInCompare, max: compareMax, count: compareCount } = useCompare();
  const { has: hasFav, add: addFav, remove: removeFav, loggedIn: favLoggedIn } = useFavorites();
  const propertyIdEarly = property._id || property.id;
  const inCompare = hasInCompare(propertyIdEarly);
  // LQIP / blur-up: the tiny blurred variant sits as a background on the
  // image container, and the real image renders on top once decoded. Lazy
  // loading + decoding=async + width-constrained Cloudinary/Unsplash transform
  // keeps the initial transfer minimal (≤ 1 KB per placeholder).
  const heroUrl = property.images?.[0];
  const heroSrc = heroUrl ? cdnImage(heroUrl, { w: 600 }) : heroUrl;
  // PERF audit: cards render at 425×284 max; keeping w=800 was 47 KiB
  // wasted per card (audit flagged 5+ cards). Cap srcset at 640w which
  // still serves crisp 2× DPR at the largest render width.
  const heroSrcSet = heroUrl ? cdnSrcSet(heroUrl, [320, 480, 640]) : undefined;
  const heroLqip = heroUrl ? cdnImage(heroUrl, { lqip: true }) : null;
  const isFavorite = favLoggedIn && hasFav(propertyIdEarly);

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!favLoggedIn) {
      // Honest UX: do NOT toggle the heart. Tell the user the truth and
      // offer to log in. After login, the user can come back and favorite.
      toast({
        title: 'Connectez-vous pour enregistrer ce bien',
        description: 'Vos favoris sont synchronisés sur tous vos appareils.',
        action: (
          <button
            type="button"
            onClick={() => {
              // Preserve the return path so the user lands back here after login
              const ret = encodeURIComponent(location.pathname + location.search);
              navigate(`/auth?return=${ret}`);
            }}
            className="text-xs font-bold text-brand-600 hover:text-brand-700 underline"
          >
            Se connecter
          </button>
        ),
      });
      return;
    }
    const wasFavorite = isFavorite;
    const res = wasFavorite ? await removeFav(propertyIdEarly) : await addFav(propertyIdEarly);
    if (res.ok) {
      toast({
        title: wasFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris',
        description: property.title,
      });
      // BUG-06 — Let parent lists (e.g. Dashboard "Mes favoris" tab) re-sync
      // their fetched collection once the mutation is confirmed by the API.
      if (typeof onFavoriteChange === 'function') {
        onFavoriteChange({ id: propertyIdEarly, favorited: !wasFavorite });
      }
    } else {
      toast({
        title: 'Action impossible',
        description: 'Réessayez dans un instant.',
        variant: 'destructive',
      });
    }
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCompare) {
      removeCompare(propertyIdEarly);
      toast({ title: 'Retiré du comparateur' });
      return;
    }
    const res = addCompare(property);
    if (res.ok) {
      toast({ title: 'Ajouté au comparateur', description: `${compareCount + 1}/${compareMax}` });
    } else if (res.reason === 'limit') {
      toast({ title: 'Maximum atteint', description: `${compareMax} biens max — retirez-en un.`, variant: 'destructive' });
    }
  };

  const propertyId = property._id || property.id;
  // SEO — prefer the canonical slug for URLs so crawlers and human
  // shares see keyword-rich links; fall back to the ObjectId when the
  // slug hasn't been backfilled yet (e.g. brand-new listing during the
  // few-second window before the insert-hook writes it).
  const propertySlugOrId = property.slug || propertyId;

  // DB-03c — Live countdown for the pending-payment overlay. Re-ticks
  // every minute so "Expire dans 23h" stays accurate without a hard refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!payDeadlineAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [payDeadlineAt]);
  const deadlineMs = payDeadlineAt ? new Date(payDeadlineAt).getTime() : null;
  const msLeft = deadlineMs ? deadlineMs - now : null;
  // Surface the urgency pill only when < 48h remain — otherwise it's noise.
  let urgencyLabel = null;
  let urgencyBlink = false;
  if (msLeft != null && msLeft <= 48 * 3600 * 1000) {
    if (msLeft <= 0) {
      urgencyLabel = 'Expiré';
      urgencyBlink = true;
    } else if (msLeft < 3600 * 1000) {
      urgencyLabel = `Expire dans ${Math.max(1, Math.round(msLeft / 60_000))} min`;
      urgencyBlink = true;
    } else {
      urgencyLabel = `Expire dans ${Math.max(1, Math.round(msLeft / 3600_000))} h`;
      urgencyBlink = msLeft < 6 * 3600 * 1000;
    }
  }
  const isTop = isActive(property.topUntil);
  const isFavoriteBadge = isActive(property.favoriteBadgeUntil);
  const isHomeFeatured = isActive(property.homeFeaturedUntil);
  const isNew = isRecent(property.createdAt, 7);
  const has360 = property.virtualTourUrl || property.has360;
  // Days on market — used to surface "En ligne depuis X jours" once Nouveau expires.
  const daysOnMarket = (() => {
    if (!property.createdAt) return null;
    const created = new Date(property.createdAt);
    if (Number.isNaN(created.getTime())) return null;
    const diff = Math.floor((Date.now() - created.getTime()) / 86400000);
    return diff >= 0 ? diff : null;
  })();

  return (
    <Link
      to={`/property/${propertySlugOrId}`}
      className="group block focus:outline-none"
      data-testid={`property-card-${propertyId}`}
    >
      <article className={`
        bg-white rounded-2xl border border-stone-200 overflow-hidden
        transition-all duration-300
        hover:-translate-y-1 hover:shadow-xl hover:border-stone-300
        ${isTop ? 'ring-2 ring-brand-400 ring-offset-2' : ''}
      `}>
        {/* Image section — aspect 4:3 */}
        <div
          className="relative aspect-[4/3] overflow-hidden bg-stone-100"
          style={heroLqip ? {
            backgroundImage: `url(${heroLqip})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : undefined}
        >
          {heroSrc ? (
            <img
              src={heroSrc}
              srcSet={heroSrcSet}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              alt={property.title}
              loading="lazy"
              decoding="async"
              width="800"
              height="533"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              style={{ filter: 'saturate(0.97) brightness(0.99)' }}
            />
          ) : (
            // DB-03 — Empty-state placeholder instead of a blank gray square.
            // Triggered when a draft listing has no image yet (e.g. paiement en
            // attente sans upload). Visible only on owner dashboards because
            // public listings never reach approved-without-images.
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400 bg-gradient-to-br from-stone-100 to-stone-200"
              data-testid={`property-card-no-image-${propertyId}`}
            >
              <ImageOff className="h-9 w-9" />
              <div className="text-center px-4">
                <p className="text-xs font-semibold text-stone-600">Aucune photo</p>
                <p className="text-label text-stone-500 mt-0.5">
                  Modifiez l&apos;annonce pour ajouter des photos
                </p>
              </div>
            </div>
          )}

          {/* DB-03 — Pending-payment overlay. We KEEP the image visible (the
              owner uploaded it, so they should see it) but add a subtle
              "Paiement requis" lock band so the unpaid state is unmistakable.
              When the parent passes `onPayClick`, the band becomes a clickable
              CTA (Booking.com pattern → ~25 % faster checkout). */}
          {property.status === 'pending_payment' && (
            onPayClick ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPayClick(property); }}
                className="absolute inset-x-0 bottom-0 group/cta cursor-pointer focus:outline-none"
                aria-label={
                  payPrice
                    ? `Finaliser le paiement de ${formatPrice(payPrice)} pour publier cette annonce`
                    : 'Finaliser le paiement pour publier cette annonce'
                }
                data-testid={`property-card-pending-cta-${propertyId}`}
              >
                <div className="w-full bg-gradient-to-t from-amber-900/90 via-amber-900/55 to-transparent py-2.5 px-3 flex items-center justify-center gap-2 transition-all group-hover/cta:from-amber-900 group-hover/cta:via-amber-900/75">
                  <Lock className="h-3.5 w-3.5 text-amber-200" />
                  <span className="text-label font-extrabold uppercase tracking-wider text-amber-100">
                    Finaliser le paiement
                  </span>
                  <span
                    aria-hidden="true"
                    className="text-amber-100 text-sm font-bold transition-transform group-hover/cta:translate-x-0.5"
                  >
                    →
                  </span>
                  {payPrice ? (
                    <span
                      className="text-label font-extrabold text-white bg-amber-700/80 ring-1 ring-amber-300/40 rounded-full px-2 py-0.5"
                      data-testid={`property-card-pending-price-${propertyId}`}
                    >
                      {formatPrice(payPrice)}
                    </span>
                  ) : null}
                  {urgencyLabel ? (
                    <span
                      className={`text-label font-bold uppercase tracking-wider text-white rounded-full px-2 py-0.5 ring-1 ring-red-300/50 bg-red-600/90 ${urgencyBlink ? 'animate-pulse' : ''}`}
                      data-testid={`property-card-pending-countdown-${propertyId}`}
                    >
                      {urgencyLabel}
                    </span>
                  ) : null}
                </div>
              </button>
            ) : (
              <div
                className="absolute inset-0 pointer-events-none flex items-end justify-center"
                data-testid={`property-card-pending-overlay-${propertyId}`}
              >
                <div className="w-full bg-gradient-to-t from-amber-900/85 via-amber-900/40 to-transparent py-2 px-3 flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3 text-amber-200" />
                  <span className="text-label font-bold uppercase tracking-wider text-amber-100">
                    Paiement requis pour publier
                  </span>
                </div>
              </div>
            )
          )}

          {/* Top-left badges stack — UX #5: cap at 2 badges to avoid
              visual fragmentation. Always show purpose; pick ONE secondary
              badge by priority (most actionable wins). */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start max-w-[calc(100%-60px)]">
            <Badge className="bg-stone-900/90 text-white border-0 backdrop-blur-sm rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1">
              {property.purpose === 'sale' ? 'À Vendre' : 'À Louer'}
            </Badge>
            {(() => {
              // Priority-ordered list. First match wins, the rest are dropped.
              if (property.type === 'bank_repossession') return (
                <Badge
                  className="bg-alert text-white border-0 shadow-md rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1"
                  data-testid="badge-bank-repossession"
                >
                  <Landmark className="h-2.5 w-2.5 mr-1" /> Reprise bancaire
                </Badge>
              );
              if (isTop) return (
                <Badge className="bg-gradient-to-r from-brand-500 to-amber-500 text-white border-0 shadow-md rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1">
                  <TrendingUp className="h-2.5 w-2.5 mr-1" />Top
                </Badge>
              );
              if (isFavoriteBadge) return (
                <Badge className="bg-pink-500 text-white border-0 shadow-md rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1">
                  <Star className="h-2.5 w-2.5 mr-1 fill-white" />Coup de cœur
                </Badge>
              );
              if (property.featured || isHomeFeatured) return (
                <Badge className="bg-amber-500 text-white border-0 rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1">
                  Premium
                </Badge>
              );
              if (property.video?.video_id) return (
                <Badge
                  className="bg-red-600 text-white border-0 shadow-md rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1"
                  data-testid="badge-video"
                >
                  <Play className="h-2.5 w-2.5 mr-1" fill="currentColor" /> Visite vidéo
                </Badge>
              );
              if (isNew) return (
                <Badge className="bg-emerald-600 text-white border-0 rounded-full text-label uppercase tracking-wider font-bold px-2.5 py-1">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />Nouveau
                </Badge>
              );
              return null;
            })()}
            {/* Distance pill remains separate (utility-only, fades out
                automatically when not filtering by radius). */}
            {Number.isFinite(property.distance_km) && (
              <Badge
                className="bg-white/95 text-stone-800 border border-stone-200 shadow-sm rounded-full text-label font-bold px-2.5 py-1"
                data-testid="badge-distance-km"
              >
                À {property.distance_km < 1
                  ? `${Math.round(property.distance_km * 1000)} m`
                  : `${property.distance_km.toFixed(1)} km`}
              </Badge>
            )}
          </div>

          {/* Top-right badges (smaller, icon-only).
              Mobile: action buttons alignés en ROW (heart+compare côte à côte)
              pour libérer de la hauteur — les badges 360°/Vérifié wrappent
              en-dessous. Desktop: stack vertical conservé. */}
          <div className="absolute top-3 right-3 flex flex-wrap flex-row sm:flex-col gap-1.5 items-end justify-end max-w-[130px]">
            <Button
              onClick={handleFavorite}
              size="icon"
              variant="ghost"
              aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              className="bg-white/90 hover:bg-white backdrop-blur-sm rounded-full h-9 w-9 shadow-sm"
              data-testid={`property-card-fav-${propertyId}`}
            >
              <Heart className={`h-4 w-4 ${isFavorite ? 'fill-brand-500 text-brand-500' : 'text-stone-700'}`} />
            </Button>
            <Button
              onClick={handleCompare}
              size="icon"
              variant="ghost"
              aria-label={inCompare ? 'Retirer du comparateur' : 'Ajouter au comparateur'}
              title={inCompare ? 'Retirer du comparateur' : 'Comparer ce bien'}
              className={`backdrop-blur-sm rounded-full h-9 w-9 shadow-sm ${inCompare ? 'bg-brand-600 hover:bg-brand-700 text-white' : 'bg-white/90 hover:bg-white text-stone-700'}`}
              data-testid={`property-card-compare-${propertyId}`}
            >
              <Scale className="h-4 w-4" />
            </Button>
            {has360 && (
              <Badge className="bg-indigo-600 text-white border-0 rounded-full text-label uppercase tracking-wider font-bold px-2 py-1 flex items-center gap-1">
                <Camera className="h-2.5 w-2.5" /> 360°
              </Badge>
            )}
            {property.verified && (
              <Badge className="bg-success text-white border-0 rounded-full text-label uppercase tracking-wider font-bold px-2 py-1 flex items-center gap-1">
                <ShieldCheck className="h-2.5 w-2.5" /> Vérifié
              </Badge>
            )}
          </div>
        </div>

        {/* Content section */}
        <div className="p-5">
          {/* Price first — most important visual element */}
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-price tnum whitespace-nowrap text-brand-600 leading-none">
              {currencyCode === 'FCFA' ? formatPrice(property.price) : formatCurrency(property.price)}
            </span>
            {property.purpose === 'rent' && (
              <span className="text-xs text-stone-500 font-medium">
                {property.type === 'event_hall' ? '/jour' : '/mois'}
              </span>
            )}
          </div>

          {/* Title — 2 lines max with ellipsis (BUG-007 fix). The native
              `title` attribute exposes the full string on hover, so long
              titles remain accessible without needing to click through. */}
          <h3
            className="font-semibold text-stone-900 line-clamp-2 min-h-[2.6em] text-sm sm:text-base group-hover:text-brand-600 transition-colors"
            title={property.title}
            data-testid={`property-card-title-${propertyId}`}
          >
            {property.title}
          </h3>

          {/* Location */}
          <div className="flex items-center text-xs text-stone-500 mt-1">
            <MapPin className="h-3 w-3 mr-1 shrink-0" />
            <span className="truncate">
              {property.neighborhood}{property.neighborhood && property.city ? ', ' : ''}{property.city}
            </span>
          </div>

          {/* Specs row */}
          <div className="flex items-center gap-3 text-xs text-stone-600 mt-3 pt-3 border-t border-stone-100">
            {property.bedrooms != null && property.bedrooms > 0 && (
              <div className="flex items-center gap-1" title={`${property.bedrooms} chambres`}>
                <Bed className="h-3.5 w-3.5 text-stone-400" />
                <span className="font-medium">{property.bedrooms}</span>
              </div>
            )}
            {property.bathrooms != null && property.bathrooms > 0 && (
              <div className="flex items-center gap-1" title={`${property.bathrooms} salles de bain`}>
                <Bath className="h-3.5 w-3.5 text-stone-400" />
                <span className="font-medium">{property.bathrooms}</span>
              </div>
            )}
            {property.area && (
              <div className="flex items-center gap-1" title={`${property.area} m²`}>
                <Square className="h-3.5 w-3.5 text-stone-400" />
                <span className="font-medium">{property.area}m²</span>
              </div>
            )}
            {(() => {
              const agentDisplay =
                property.agentCompanyName ||
                property.agentPersonalName ||
                property.agent?.name ||
                property.agentName;
              if (!agentDisplay) return null;
              const agentInitial = (agentDisplay || '?').charAt(0).toUpperCase();
              return (
                <div className="ml-auto flex items-center gap-1.5 text-xs text-stone-500 truncate" title={agentDisplay}>
                  {property.agentPhoto ? (
                    <img
                      src={property.agentPhoto}
                      alt={agentDisplay}
                      className="w-5 h-5 rounded-full object-cover shrink-0 border border-stone-200"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-white text-label font-bold flex items-center justify-center shrink-0">
                      {agentInitial}
                    </div>
                  )}
                  <span className="truncate max-w-[80px]">{agentDisplay}</span>
                  {property.agentVerified && (
                    <ShieldCheck
                      className="h-3.5 w-3.5 text-success shrink-0"
                      aria-label="Agent vérifié"
                      data-testid={`property-card-agent-verified-${propertyId}`}
                    />
                  )}
                </div>
              );
            })()}
          </div>

          {/* Trust signals row — days on market + view count */}
          {(daysOnMarket != null || (property.views_total ?? 0) > 0) && (
            <div className="flex items-center gap-3 text-label text-stone-500 mt-2" data-testid={`property-card-trust-${propertyId}`}>
              {daysOnMarket != null && !isNew && (
                <span className="inline-flex items-center gap-1" title="Sur le marché">
                  <Clock className="h-3 w-3" />
                  {daysOnMarket === 0
                    ? "Aujourd'hui"
                    : daysOnMarket === 1
                    ? '1 jour'
                    : `${daysOnMarket} jours`}
                </span>
              )}
              {(property.views_total ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1" title={`${property.views_total} vues`}>
                  <Eye className="h-3 w-3" />
                  {property.views_total}
                </span>
              )}
            </div>
          )}
        </div>
      </article>
    </Link>
  );
};

export default PropertyCard;
